import { Pointer } from "rfc6902";
import { mutateByPath, removeByPath } from "../../utility";
import { FigureData } from "../types/FigureData";
import { FigureGroupData } from "../types/FigureGroupData";
import { FigureReferenceLinesData } from "../types/FigureReferenceLinesData";
import { FigureRegressionLineData } from "../types/FigureRegressionLineData";
import { fillPropertiesArray } from "./fillPropertiesArray";
import {
  ADD_DATA_GROUP,
  ADD_PROPERTY,
  ADD_REFERENCE_LINE,
  ADD_REGRESSION_LINE,
  BeFigureAction,
  BeFigureState,
  CHANGE_REGRESSION_LINE_TYPE,
  FIGURE_CHANGE_SENT,
  LOAD_FIGURE_INPUT,
  PATCH_FIGURE_STATE,
  REMOVE_FIGURE_ITEM,
  SET_FIGURE_TYPE,
  SET_LEGEND_TYPE,
} from "./interface";

/**
 * Reducer function to manage BeFigureState state
 *
 * @param state - Current state of BeFigureState
 * @param action - Action to be performed on the state
 * @returns Updated state of BeFigureState
 */
export const figureReducer = (state: BeFigureState, action: BeFigureAction): BeFigureState => {
  switch (action.operation) {
    case ADD_DATA_GROUP: {
      if (state.figureInput.dataGroups.length === action.newLength - 1) {
        const newDataGroup: FigureGroupData = {
          id: crypto.randomUUID(),
          dataGroupName: `DataGroup_${action.newLength}`,
          dataGroupLabel: `Data Group ${action.newLength}`,
          properties: fillPropertiesArray(
            state.figureSettings.figureTypeSettings.find(
              (ft) => ft.value === state.figureInput.figureType,
            )?.dataRowVars ?? [],
          ),
        };
        return {
          ...state,
          changed: true,
          figureInput: {
            ...state.figureInput,
            dataGroups: [...state.figureInput.dataGroups, newDataGroup],
          },
        };
      }
      return state;
    }
    case ADD_PROPERTY: {
      const newProperty = { varName: action.varName, value: "" };
      const pointer = Pointer.fromJSON(action.pointer);
      const currentProperties = pointer.get(state.figureInput);
      const newProperties = [...currentProperties, newProperty];
      const updatedFigureInput = mutateByPath(state.figureInput, action.pointer, newProperties);
      return {
        ...state,
        changed: true,
        figureInput: updatedFigureInput,
      };
    }
    case ADD_REFERENCE_LINE: {
      if (state.figureInput.referenceLines.length === action.newLength - 1) {
        const newReferenceLine: FigureReferenceLinesData = {
          id: crypto.randomUUID(),
          properties: fillPropertiesArray(state.figureSettings.figureReferenceLinesSettings ?? []),
        };
        return {
          ...state,
          changed: true,
          figureInput: {
            ...state.figureInput,
            referenceLines: [...state.figureInput.referenceLines, newReferenceLine],
          },
        };
      }
      return state;
    }
    case ADD_REGRESSION_LINE: {
      if (state.figureInput.regressionLines.length === action.newLength - 1) {
        const defaultRLSettings = state.figureSettings.figureRegressionLineSettings[0];
        const newRegressionLine: FigureRegressionLineData = {
          id: crypto.randomUUID(),
          lineType: defaultRLSettings?.lineType || "linear",
          properties: fillPropertiesArray(defaultRLSettings?.parameters ?? []),
        };
        return {
          ...state,
          changed: true,
          figureInput: {
            ...state.figureInput,
            regressionLines: [...state.figureInput.regressionLines, newRegressionLine],
          },
        };
      }
      return state;
    }
    case CHANGE_REGRESSION_LINE_TYPE: {
      const newLineTypeSettings = state.figureSettings.figureRegressionLineSettings.find(
        (rls) => rls.lineType === action.lineType,
      );
      const thisRegressionLine = Pointer.fromJSON(action.pointer).get(state.figureInput);
      const newRegressionLine: FigureRegressionLineData = {
        ...thisRegressionLine,
        lineType: action.lineType,
        properties: fillPropertiesArray(
          newLineTypeSettings?.parameters ?? [],
          thisRegressionLine.properties,
        ),
      };
      const updatedFigureInput = mutateByPath(state.figureInput, action.pointer, newRegressionLine);
      return {
        ...state,
        changed: true,
        figureInput: updatedFigureInput,
      };
    }
    case FIGURE_CHANGE_SENT: {
      return { ...state, changed: false };
    }
    case LOAD_FIGURE_INPUT: {
      return {
        ...state,
        changed: false,
        figureInput: action.figureInput,
        savedFigureData: {},
      };
    }
    case PATCH_FIGURE_STATE: {
      const updatedFigureInput = mutateByPath(state.figureInput, action.pointer, action.value);
      return {
        ...state,
        changed: true,
        figureInput: updatedFigureInput,
      };
    }
    case REMOVE_FIGURE_ITEM: {
      const updatedFigureInput = removeByPath(state.figureInput, action.dataPath);
      return {
        ...state,
        changed: true,
        figureInput: updatedFigureInput,
      };
    }
    case SET_FIGURE_TYPE: {
      const oldFigureType = state.figureInput.figureType;
      const newFigureTypeSettings = state.figureSettings.figureTypeSettings.find(
        (ft) => ft.value === action.newValue,
      );

      // Save current figure data before switching
      const savedFigureData: Record<string, Partial<FigureData>> = {
        ...(state.savedFigureData ?? {}),
        [oldFigureType]: {
          figureTypeProperties: state.figureInput.figureTypeProperties,
          dataGroups: state.figureInput.dataGroups,
          regressionLines: state.figureInput.regressionLines,
        },
      };

      // Check if we have saved data for the new figure type
      const restoredData = savedFigureData[action.newValue];

      let dataGroups: FigureGroupData[];
      const figureTypeProperties = fillPropertiesArray(
        newFigureTypeSettings?.properties ?? [],
        restoredData?.figureTypeProperties,
      );
      const regressionLines = restoredData?.regressionLines ?? [];

      if (restoredData?.dataGroups && restoredData.dataGroups.length > 0) {
        // Restore saved data groups
        dataGroups = restoredData.dataGroups.map((dg: FigureGroupData) => ({
          ...dg,
          properties: fillPropertiesArray(newFigureTypeSettings?.dataRowVars ?? [], dg.properties),
        }));
      } else {
        // Create default data groups
        const numberOfSeries = newFigureTypeSettings?.defaultNumberOfSeries ?? 1;
        const defaultSeries = newFigureTypeSettings?.defaultSeries ?? [];
        dataGroups = [];
        for (let i = 0; i < numberOfSeries; i++) {
          const thisDefaultSeries =
            defaultSeries.length > 0 ? defaultSeries[i % defaultSeries.length] : undefined;
          const defaultGroupName = thisDefaultSeries?.dataGroupName ?? "DataGroup";
          const defaultGroupLabel = thisDefaultSeries?.dataGroupLabel ?? "Data Group";
          const newDataGroup: FigureGroupData = {
            id: crypto.randomUUID(),
            dataGroupName: `${defaultGroupName}`,
            dataGroupLabel: `${defaultGroupLabel}`,
            properties: fillPropertiesArray(
              newFigureTypeSettings?.dataRowVars ?? [],
              thisDefaultSeries?.properties,
            ),
          };
          dataGroups.push(newDataGroup);
        }
      }

      return {
        ...state,
        changed: true,
        savedFigureData,
        figureInput: {
          ...state.figureInput,
          figureType: action.newValue,
          figureTypeProperties,
          dataGroups,
          regressionLines,
        },
      };
    }
    case SET_LEGEND_TYPE: {
      const legendTypeSettings = state.figureSettings.figureLegendSettings.find(
        (lt) => lt.legendType === action.newValue,
      );
      const updatedLegend = state.figureInput.legend.map((li) => ({
        ...li,
        properties: fillPropertiesArray(legendTypeSettings?.parameters ?? [], li.properties),
      }));
      return {
        ...state,
        changed: true,
        figureInput: {
          ...state.figureInput,
          legendType: action.newValue,
          legend: updatedLegend,
        },
      };
    }
  }
};
